import EventEmitter from '../Utils/EventEmitter';
import { Camera } from '../Core/Camera';
import { Sizes } from '../Core/Sizes';
import { SmoothScroll } from '../Core/SmoothScroll';
import { SectionFactory } from './SectionFactory';

/**
 * SectionManager class
 * Manages sections and their layout in 3D space
 */
export class SectionManager extends EventEmitter {
  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {SectionManager} The singleton instance
   */
  static getInstance() {
    if (!SectionManager.instance) {
      SectionManager.instance = new SectionManager();
    }
    return SectionManager.instance;
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (SectionManager.instance) {
      return SectionManager.instance;
    }

    super();
    
    // Set up dependencies
    this.camera = Camera.getInstance();
    this.sizes = Sizes.getInstance();
    this.scroll = SmoothScroll.getInstance();

    // Section data
    this.sections = document.querySelectorAll('[data-section]');
    this.sectionData = [];
    this.sectionInstances = [];

    // Initialize resize observer
    this.resizeObserver = new ResizeObserver(this.handleSectionResize.bind(this));

    // Calculate initial layout immediately, don't wait for scroll ready
    this.calculateSectionLayout();

    // Initialize sections after layout calculation
    this.initializeSections();

    // Set up event listeners
    this.setupEventListeners();

    // Set instance
    SectionManager.instance = this;
  }

  /**
   * Handle section resize events
   * @param {ResizeObserverEntry[]} entries - Array of resize observer entries
   */
  handleSectionResize(entries) {
    if (this.sizes.isResizing) return;

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.resizeTimeout = null;
      
      if (this.sizes.isResizing) return;

      this.calculateSectionLayout();
      this.updateSectionInstances();
    }, 200);
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Handle resize
    this.sizes.on('resize', () => {
      // Clear any pending observer resize
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
      
      this.calculateSectionLayout();
      this.updateSectionInstances();
    });

    // Handle scroll
    this.scroll.on('scroll', () => {
      this.updateSectionsVisibility();
      this.updateSectionProgress();
    });

    // Observe all sections for size changes
    this.sections.forEach(section => {
      this.resizeObserver.observe(section);
    });
  }

  /**
   * Calculate section layout in 3D space
   */
  calculateSectionLayout() {
    this.sectionData = [];

    // Calculate initial vertical position in world space (camera frustum center)
    const startingPositionY = this.sizes.frustumSize.height / 2;
    const scrollY = this.scroll.getScrollY();
    
    // Process each section
    this.sections.forEach((section, index) => {
      const rect = section.getBoundingClientRect();
    
      // Calculate world dimensions
      const worldHeight = this.sizes.pixelsToWorldUnits(rect.height);
      const worldWidth = this.sizes.pixelsToWorldUnits(this.sizes.width);

      // Store the HTML dimensions
      const htmlData = {
        top: rect.top + scrollY,
        bottom: rect.top + scrollY + rect.height,
        height: rect.height,
        width: this.sizes.width
      };

      // Calculate world position directly from DOM position
      const worldPosition = {
        y: startingPositionY - this.sizes.pixelsToWorldUnits(htmlData.top) - (worldHeight / 2),
        z: 0
      };

      const sectionIndex = this.sizes.isComingSoon && index > 0 ? index + 99 : index;

      // Create section data object
      this.sectionData.push({
        element: section,
        index: sectionIndex,
        htmlData: htmlData,
        worldData: {
          position: worldPosition,
          width: worldWidth,
          height: worldHeight
        },
        color: section.dataset.backgroundColor || this.getColorForIndex(index),
        progress: 0,
        visiblePercent: 0,
        visible: false
      });
    });

    // Update camera and trigger events
    this.trigger('layoutUpdated', [this.sectionData]);
    this.updateSectionsVisibility();
  }

  /**
   * Initialize section instances
   */
  initializeSections() {
    // Clear existing section instances
    this.disposeSectionInstances();
    this.sectionInstances = [];
    
    // Create section instances using factory
    this.sectionData.forEach(sectionData => {
      const section = SectionFactory.createSection(sectionData);
      this.sectionInstances.push(section);
    });
    
    // Trigger sections initialized event
    this.trigger('sectionsInitialized', [this.sectionInstances]);
  }

  /**
   * Update section instances with new layout data
   */
  updateSectionInstances() {
    // Update each section instance with its respective section data
    this.sectionInstances.forEach((section, index) => {
      if (index < this.sectionData.length) {
        section.updateSectionData(this.sectionData[index]);
      }
    });
    
    // Trigger sections updated event
    this.trigger('sectionsUpdated', [this.sectionInstances]);
  }

  /**
   * Update sections visibility based on scroll position
   * @param {Number} scrollY - Scroll position in pixels
   */
  updateSectionsVisibility() {
    const { viewportTop, viewportBottom } = this.camera.calculateCameraViewport();
    
    // Add buffer zone (20% of viewport height)
    const bufferSize = (viewportTop - viewportBottom) * 0.1;
    const bufferedViewportTop = viewportTop + bufferSize;
    const bufferedViewportBottom = viewportBottom - bufferSize;
    
    // Check each section
    this.sectionData.forEach((section, index) => {
      // Get section's world position
      const sectionTop = section.worldData.position.y + section.worldData.height / 2;
      const sectionBottom = section.worldData.position.y - section.worldData.height / 2;
      
      // Section is visible if it overlaps with buffered viewport
      const wasVisible = section.visible;
      section.visible = !(sectionBottom >= bufferedViewportTop || sectionTop <= bufferedViewportBottom);
      
      // Calculate visible percentage using buffered viewport
      if (section.visible) {
        const visibleTop = Math.max(sectionBottom, bufferedViewportBottom);
        const visibleBottom = Math.min(sectionTop, bufferedViewportTop);
        const visibleHeight = visibleBottom - visibleTop;
        section.visiblePercent = visibleHeight / section.worldData.height;
      } else {
        section.visiblePercent = 0;
      }

      // Trigger visibility change event
      if (wasVisible !== section.visible) {
        this.sectionInstances[index]?.changeVisibility(section.visible);
      }
    });
  }

  /**
   * Update sections progress and animation
   */
  updateSectionProgress() {
    const { viewportTop, viewportBottom } = this.camera.calculateCameraViewport();

    this.sectionData.forEach((sectionData, index) => {
      // Get section's world position
      const sectionTop = sectionData.worldData.position.y + sectionData.worldData.height / 2;
      const sectionBottom = sectionData.worldData.position.y - sectionData.worldData.height / 2;

      let sectionProgress = 0;

      if (sectionTop <= viewportBottom) {
        sectionProgress = 0;
      } else if (sectionBottom >= viewportTop) {
        sectionProgress = 1;
      } else {
        const totalScrollDistance = sectionTop - viewportBottom + (viewportTop - sectionBottom);
        const scrolledDistance = sectionTop - viewportBottom;
        sectionProgress = Math.max(0, Math.min(1, scrolledDistance / totalScrollDistance));
      }

      // Update section data
      sectionData.progress = sectionProgress;
      
      // Update corresponding section instance
      if (this.sectionInstances[index]) {
        this.sectionInstances[index].update(sectionData.progress);
      }
    });
  }

  /**
   * Get a color for a section based on its index
   * @param {Number} index - Section index
   * @returns {String} - HEX color
   */
  getColorForIndex(index) {
    const colors = [
      '#144e7a',
      '#1a84f3',
      '#1780ff',
      '#2b66ae',
      '#1780ff'
    ];

    return colors[index % colors.length];
  }

  /**
   * Get section data
   * @returns {Array} - Array of section data objects
   */
  getSections() {
    return this.sectionData;
  }

  /**
   * Get section instances
   * @returns {Array} - Array of section instances
   */
  getSectionInstances() {
    return this.sectionInstances;
  }

  /**
   * Get a specific section by index
   * @param {Number} index - Section index
   * @returns {Object} - Section data object
   */
  getSection(index) {
    return this.sectionData[index];
  }

  /**
   * Get a specific section instance by index
   * @param {Number} index - Section index
   * @returns {Section} - Section instance
   */
  getSectionInstance(index) {
    return this.sectionInstances[index];
  }

  /**
   * Get section currently in center of viewport
   * @returns {Object} - Section data object
   */
  getCurrentSection() {
    let maxVisibleSection = null;
    let maxVisibleArea = 0;

    this.sectionData.forEach(section => {
      if (section.visible && section.visiblePercent > maxVisibleArea) {
        maxVisibleArea = section.visiblePercent;
        maxVisibleSection = section;
      }
    });

    return maxVisibleSection;
  }

  /**
   * Dispose section instances
   */
  disposeSectionInstances() {
    if (this.sectionInstances && this.sectionInstances.length > 0) {
      this.sectionInstances.forEach(section => {
        if (section && typeof section.dispose === 'function') {
          section.dispose();
        }
      });
    }
    this.sectionInstances = [];
  }

  /**
   * Dispose all resources
   */
  dispose() {
    // Dispose section instances
    this.disposeSectionInstances();
    
    // Remove event listeners
    this.sizes.off('resize');
    this.scroll.off('scroll');
    
    // Disconnect resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    // Clear timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Clear data
    this.sectionData = [];
    this.sections = null;
    
    // Trigger disposed event
    this.trigger('disposed');
  }
}
